import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Maximize2, SearchZoomIn, SearchZoomOut } from '@/components/icons';
import { Button } from '@/components/ui/button';

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;
/** Wheel delta → zoom sensitivity (ctrl/cmd + wheel). */
const WHEEL_SENSITIVITY = 0.003;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

interface ChartZoomViewportProps {
    children: React.ReactNode;
}

/**
 * Presentation-agnostic zoom + pan viewport. Zoom via ctrl/cmd + wheel or the
 * floating +/− pill (1x–4x); drag to pan while zoomed. It magnifies whatever it
 * wraps with a CSS transform, so it stays chart-unaware and reusable.
 */
export default function ChartZoomViewport({ children }: ChartZoomViewportProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [zoom, setZoom] = useState(MIN_ZOOM);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    // Live drag anchor: pointer position + pan offset captured on pointerdown.
    const dragStart = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);

    // Keep pan within bounds so the scaled content can't be dragged out of view.
    // Max offset per axis ≈ half of the overflow the zoom introduces.
    const clampPan = useCallback((next: { x: number; y: number }, atZoom: number) => {
        const el = containerRef.current;
        if (!el || atZoom <= MIN_ZOOM) return { x: 0, y: 0 };
        const maxX = ((atZoom - 1) * el.clientWidth) / 2;
        const maxY = ((atZoom - 1) * el.clientHeight) / 2;
        return { x: clamp(next.x, -maxX, maxX), y: clamp(next.y, -maxY, maxY) };
    }, []);

    const applyZoom = useCallback(
        (nextZoom: number) => {
            const clamped = clamp(nextZoom, MIN_ZOOM, MAX_ZOOM);
            setZoom(clamped);
            setPan((prev) => clampPan(prev, clamped));
        },
        [clampPan],
    );

    // Native, non-passive wheel listener so preventDefault() actually suppresses
    // the browser's ctrl+wheel page zoom (React's onWheel is passive).
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const onWheel = (e: WheelEvent) => {
            if (!(e.ctrlKey || e.metaKey)) return;
            e.preventDefault();
            // Stop the report's global window-level zoom (useReportZoom) from also
            // firing on this same ctrl/cmd+wheel while the modal is open.
            e.stopPropagation();
            setZoom((prev) => {
                const clamped = clamp(prev - e.deltaY * WHEEL_SENSITIVITY, MIN_ZOOM, MAX_ZOOM);
                setPan((p) => clampPan(p, clamped));
                return clamped;
            });
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [clampPan]);

    const handlePointerDown = (e: React.PointerEvent) => {
        if (zoom <= MIN_ZOOM) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        dragStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
        setIsDragging(true);
    };

    const handlePointerMove = (e: React.PointerEvent) => {
        if (!dragStart.current) return;
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        setPan(clampPan({ x: dragStart.current.panX + dx, y: dragStart.current.panY + dy }, zoom));
    };

    const endDrag = (e: React.PointerEvent) => {
        if (!dragStart.current) return;
        e.currentTarget.releasePointerCapture(e.pointerId);
        dragStart.current = null;
        setIsDragging(false);
    };

    const reset = () => {
        setZoom(MIN_ZOOM);
        setPan({ x: 0, y: 0 });
    };

    const canPan = zoom > MIN_ZOOM;

    return (
        <div ref={containerRef} className="relative size-full overflow-hidden">
            {/* Pan surface: pointer handlers live here (NOT on the container) so
                pointer-capture never swallows clicks on the sibling control pill. */}
            <div
                className="size-full"
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                style={{
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                    transformOrigin: 'center center',
                    transition: isDragging ? 'none' : 'transform 120ms ease-out',
                    cursor: canPan ? (isDragging ? 'grabbing' : 'grab') : 'default',
                }}
            >
                {children}
            </div>

            <div
                className="absolute bottom-3 right-3 z-10 flex items-center gap-2 rounded-full border border-border/50 bg-card/80 px-3 py-1.5 shadow-xl backdrop-blur-md"
                onPointerDown={(e) => e.stopPropagation()}
            >
                <Button
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    onClick={() => applyZoom(zoom - ZOOM_STEP)}
                    disabled={zoom <= MIN_ZOOM}
                    aria-label="Diminuir zoom"
                >
                    <SearchZoomOut className="size-4" />
                </Button>

                <span className="min-w-[42px] text-center text-xs font-medium tabular-nums">
                    {Math.round(zoom * 100)}%
                </span>

                <Button
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    onClick={() => applyZoom(zoom + ZOOM_STEP)}
                    disabled={zoom >= MAX_ZOOM}
                    aria-label="Aumentar zoom"
                >
                    <SearchZoomIn className="size-4" />
                </Button>

                <Button
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    onClick={reset}
                    disabled={!canPan}
                    aria-label="Restaurar zoom"
                >
                    <Maximize2 className="size-4" />
                </Button>
            </div>
        </div>
    );
}
